export const MIND_ROUND_SCORE_MAX = 100;
export const MIND_ROUND_SCORE_PENALTY = 0;
export const MIND_ROUND_SCORE_FLOOR = 100;

const RULE_HINTS = Object.freeze({
  warmTone: ['warm', 'warm-tone', 'caldo', 'tono caldo'],
  coolTone: ['cool', 'cold', 'fredd', 'frio', 'tono frio'],
  roundedShape: ['rounded', 'soft', 'rounded-shape', 'arrotond', 'redondead', 'curved'],
  angularShape: ['angular', 'sharp', 'angular-shape', 'angolar'],
});

const SHAPE_PRESENTATION = Object.freeze({
  circle: { glyph: '●', group: 'rounded' },
  square: { glyph: '■', group: 'angular' },
  triangle: { glyph: '▲', group: 'angular' },
  diamond: { glyph: '◆', group: 'angular' },
  oval: { glyph: '⬭', group: 'rounded' },
  pill: { glyph: '▰', group: 'rounded' },
  hex: { glyph: '⬢', group: 'angular' },
  ring: { glyph: '◌', group: 'rounded' },
});

const TRAIT_COPY = Object.freeze({
  'en-US': {
    warm: 'Warm',
    cool: 'Cool',
    rounded: 'Rounded',
    angular: 'Angular',
    accessibility: (label, shape, tone, printed) => `${label}, ${shape} shape, ${tone} tone, printed word ${printed}`,
  },
  'it-IT': {
    warm: 'Caldo',
    cool: 'Freddo',
    rounded: 'Arrotondata',
    angular: 'Angolare',
    accessibility: (label, shape, tone, printed) => `${label}, forma ${shape.toLocaleLowerCase()}, tonalità ${tone}, parola scritta ${printed}`,
  },
  'es-ES': {
    warm: 'Cálido',
    cool: 'Frío',
    rounded: 'Redondeada',
    angular: 'Angular',
    accessibility: (label, shape, tone, printed) => `${label}, forma ${shape.toLocaleLowerCase()}, tono ${tone}, palabra escrita ${printed}`,
  },
});

function normalizeLocale(locale = 'en-US') {
  return TRAIT_COPY[locale] ? locale : 'en-US';
}

function inferToneKey(card = {}) {
  if (card.toneKey === 'warm' || card.toneKey === 'cool') {
    return card.toneKey;
  }
  const text = normalizeText(card.metaLabel, card.toneLabel);
  if (includesHint(text, RULE_HINTS.warmTone)) {
    return 'warm';
  }
  if (includesHint(text, RULE_HINTS.coolTone)) {
    return 'cool';
  }
  return '';
}

function inferShapeGroup(card = {}) {
  if (card.shapeGroup === 'rounded' || card.shapeGroup === 'angular') {
    return card.shapeGroup;
  }
  const sourceShapeId = card.symbolType || card.sourceShapeId;
  if (SHAPE_PRESENTATION[sourceShapeId]?.group) {
    return SHAPE_PRESENTATION[sourceShapeId].group;
  }
  const text = normalizeText(card.metaLabel, card.shapeGroupLabel);
  if (includesHint(text, RULE_HINTS.roundedShape)) {
    return 'rounded';
  }
  if (includesHint(text, RULE_HINTS.angularShape)) {
    return 'angular';
  }
  return '';
}

function enrichTraitCard(
  card = {},
  index = 0,
  locale = 'en-US',
  allowToneConflict = false,
  forcedDisplayToneKey = ''
) {
  const resolvedLocale = normalizeLocale(locale);
  const copy = TRAIT_COPY[resolvedLocale];
  const sourceShapeId = card.symbolType || card.sourceShapeId;
  const toneKey = inferToneKey(card);
  const shapeGroup = inferShapeGroup(card);
  const displayToneKey = toneKey
    ? forcedDisplayToneKey || card.displayToneKey || (allowToneConflict && index % 2 === 1
      ? (toneKey === 'warm' ? 'cool' : 'warm')
      : toneKey)
    : '';

  return {
    ...card,
    glyph: card.glyph || SHAPE_PRESENTATION[sourceShapeId]?.glyph,
    toneKey: toneKey || card.toneKey,
    toneLabel: toneKey ? copy[toneKey] : card.toneLabel,
    displayToneKey: displayToneKey || card.displayToneKey,
    displayToneLabel: displayToneKey ? copy[displayToneKey] : card.displayToneLabel,
    shapeGroup: shapeGroup || card.shapeGroup,
    shapeGroupLabel: shapeGroup ? copy[shapeGroup] : card.shapeGroupLabel,
    accessibilityLabel: toneKey
      ? copy.accessibility(
        card.label || '',
        copy[shapeGroup] || shapeGroup || '',
        copy[toneKey],
        copy[displayToneKey || toneKey]
      )
      : card.accessibilityLabel,
  };
}

function enrichTraitCards(cards = [], locale = 'en-US', allowToneConflict = false) {
  if (!Array.isArray(cards)) {
    return [];
  }
  const toneOccurrences = { warm: 0, cool: 0 };
  return cards.map((card, index) => {
    const toneKey = inferToneKey(card);
    const toneIndex = toneKey ? toneOccurrences[toneKey] : 0;
    if (toneKey) {
      toneOccurrences[toneKey] += 1;
    }
    const forcedDisplayToneKey = allowToneConflict && toneKey
      ? toneIndex % 2 === 0
        ? toneKey
        : toneKey === 'warm' ? 'cool' : 'warm'
      : '';
    return enrichTraitCard(card, index, locale, allowToneConflict, forcedDisplayToneKey);
  });
}

function normalizeText(...parts) {
  return parts
    .flat()
    .filter((part) => typeof part === 'string' && part.trim().length > 0)
    .join(' ')
    .toLowerCase();
}

function includesHint(text, hints = []) {
  return hints.some((hint) => text.includes(hint));
}

function dedupeIds(ids = []) {
  return [...new Set((Array.isArray(ids) ? ids : []).map((value) => String(value)))];
}

function buildRoundRuleText(round = {}) {
  return normalizeText(
    round.targetRuleKey,
    round.targetRuleLabel,
    round.secondaryRuleLabel,
    round.prompt,
    round.subtitle,
    round.title
  );
}

function inferRulePredicate(round = {}) {
  const text = buildRoundRuleText(round);
  const wantsWarm = includesHint(text, RULE_HINTS.warmTone);
  const wantsCool = includesHint(text, RULE_HINTS.coolTone);
  const wantsRounded = includesHint(text, RULE_HINTS.roundedShape);
  const wantsAngular = includesHint(text, RULE_HINTS.angularShape);
  const predicates = [];

  if (wantsWarm && !wantsCool) {
    predicates.push((card) => card?.toneKey === 'warm');
  } else if (wantsCool && !wantsWarm) {
    predicates.push((card) => card?.toneKey === 'cool');
  }

  if (wantsRounded && !wantsAngular) {
    predicates.push((card) => card?.shapeGroup === 'rounded');
  } else if (wantsAngular && !wantsRounded) {
    predicates.push((card) => card?.shapeGroup === 'angular');
  }

  if (predicates.length === 0) {
    return null;
  }

  return (card) => predicates.every((predicate) => predicate(card));
}

function resolveTapTargetIds(round = {}) {
  const optionCards = Array.isArray(round.optionCards) ? round.optionCards : [];
  const inferredPredicate = inferRulePredicate(round);

  if (optionCards.length > 0 && inferredPredicate) {
    const inferredIds = optionCards.filter(inferredPredicate).map((card) => String(card.id));
    if (inferredIds.length > 0) {
      return dedupeIds(inferredIds);
    }
  }

  return dedupeIds(round.targetIds);
}

function resolveFilteredSequenceOrder(round = {}) {
  const previewCards = Array.isArray(round.previewCards)
    ? round.previewCards
    : Array.isArray(round.optionCards)
      ? round.optionCards
      : [];
  const ruleKey = round?.filterDefinition?.ruleKey;
  const predicatesByRule = {
    warmTone: (card) => card?.toneKey === 'warm',
    coolTone: (card) => card?.toneKey === 'cool',
    roundedShape: (card) => card?.shapeGroup === 'rounded',
    angularShape: (card) => card?.shapeGroup === 'angular',
    warmRounded: (card) => card?.toneKey === 'warm' && card?.shapeGroup === 'rounded',
    coolAngular: (card) => card?.toneKey === 'cool' && card?.shapeGroup === 'angular',
    notAngular: (card) => card?.shapeGroup !== 'angular',
    oddPosition: (card) => Number(card?.sequencePosition) % 2 === 1,
    evenPosition: (card) => Number(card?.sequencePosition) % 2 === 0,
  };
  const inferredPredicate = predicatesByRule[ruleKey] || inferRulePredicate(round);

  if (previewCards.length > 0 && inferredPredicate) {
    const inferredIds = previewCards.filter(inferredPredicate).map((card) => String(card.id));
    if (inferredIds.length > 0) {
      return dedupeIds(inferredIds);
    }
  }

  return dedupeIds(round.correctOrderIds);
}

function resolveRotationOrder(round = {}) {
  const optionCards = Array.isArray(round.optionCards) ? round.optionCards : [];
  const cardsWithRank = optionCards
    .filter((card) => Number.isFinite(Number(card?.progressionRank ?? card?.angle)));

  if (cardsWithRank.length === optionCards.length && cardsWithRank.length > 0) {
    const direction = round?.rotationReference?.direction || 'clockwise';
    return [...cardsWithRank]
      .sort((left, right) => {
        const leftAngle = Number(left.progressionRank ?? left.angle);
        const rightAngle = Number(right.progressionRank ?? right.angle);
        const leftRank = direction === 'counterclockwise' ? ((360 - leftAngle) % 360) : leftAngle;
        const rightRank = direction === 'counterclockwise' ? ((360 - rightAngle) % 360) : rightAngle;
        return leftRank - rightRank;
      })
      .map((card) => String(card.id));
  }

  return dedupeIds(round.correctOrderIds);
}

function resolveRuleStages(round = {}) {
  const optionCards = Array.isArray(round.optionCards) ? round.optionCards : [];
  const existingStages = Array.isArray(round.stages) ? round.stages : [];
  const cardsWithCategory = optionCards.filter((card) => typeof card?.category === 'string' && card.category.trim().length > 0);

  if (cardsWithCategory.length === optionCards.length && optionCards.length > 0) {
    const stageOrder = dedupeIds(existingStages.map((stage) => stage?.id)).filter(Boolean);
    const orderedIds = stageOrder.length > 0 ? stageOrder : ['longevity', 'performance'];
    const resolvedStages = orderedIds
      .map((stageId) => {
        const existingStage = existingStages.find((stage) => stage?.id === stageId) || {};
        const targetIds = optionCards
          .filter((card) => card.category === stageId)
          .map((card) => String(card.id));

        return {
          ...existingStage,
          id: stageId,
          targetIds: dedupeIds(targetIds),
        };
      })
      .filter((stage) => stage.targetIds.length > 0);

    if (resolvedStages.length > 0) {
      return resolvedStages;
    }
  }

  return existingStages.map((stage) => ({
    ...stage,
    targetIds: dedupeIds(stage?.targetIds),
  }));
}

export function arraysEqual(left = [], right = []) {
  if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
    return false;
  }
  return left.every((value, index) => value === right[index]);
}

export function unorderedArraysEqual(left = [], right = []) {
  if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
    return false;
  }

  const normalizedLeft = [...left].sort();
  const normalizedRight = [...right].sort();
  return arraysEqual(normalizedLeft, normalizedRight);
}

export function calculateMindRoundScore(failedChecks = 0, scoring = {}) {
  const roundScoreMax = Number(scoring.roundScoreMax ?? MIND_ROUND_SCORE_MAX);
  const roundScorePenalty = Number(scoring.roundScorePenalty ?? MIND_ROUND_SCORE_PENALTY);
  const roundScoreFloor = Number(scoring.roundScoreFloor ?? MIND_ROUND_SCORE_FLOOR);
  const normalizedFailedChecks = Math.max(0, Math.floor(Number(failedChecks) || 0));

  return Math.max(
    roundScoreFloor,
    roundScoreMax - (normalizedFailedChecks * roundScorePenalty)
  );
}

export function normalizeMindRound(round = {}, locale = 'en-US') {
  if (!round || typeof round !== 'object') {
    return round;
  }

  const usesTraitCards = round.interactionType === 'tap_targets'
    || round.interactionType === 'filtered_sequence'
    || round.interactionType === 'rule_switch';
  const preparedRound = usesTraitCards
    ? {
      ...round,
      optionCards: enrichTraitCards(round.optionCards, locale, true),
      previewCards: enrichTraitCards(round.previewCards, locale, true),
    }
    : round;

  if (round.interactionType === 'tap_targets') {
    return {
      ...preparedRound,
      targetIds: resolveTapTargetIds(preparedRound),
    };
  }

  if (round.interactionType === 'filtered_sequence') {
    return {
      ...preparedRound,
      correctOrderIds: resolveFilteredSequenceOrder(preparedRound),
    };
  }

  if (round.interactionType === 'rule_switch') {
    return {
      ...preparedRound,
      stages: resolveRuleStages(preparedRound),
    };
  }

  if (round.interactionType === 'reorder') {
    const isRotationRound = round.archetype === 'mental_rotation'
      || (Array.isArray(round.optionCards) && round.optionCards.some((card) => Number.isFinite(Number(card?.angle))));
    return {
      ...round,
      correctOrderIds: resolveRotationOrder(round),
      optionCards: isRotationRound
        ? (round.optionCards || []).map((card) => ({
          ...card,
          label: 'Vector',
          glyph: '⬆',
          angleLabel: undefined,
          rotationDegrees: Number(card.angle),
          metaTokens: [],
        }))
        : round.optionCards,
    };
  }

  return round;
}

export function normalizeMindRounds(rounds = [], locale = 'en-US') {
  return Array.isArray(rounds) ? rounds.map((round) => normalizeMindRound(round, locale)) : [];
}

export function buildMindSessionSummary({ archetype = '', rounds = [], roundResults = [], sessionBadge = '' }) {
  const normalizedRoundResults = roundResults.map((result, index) => ({
    roundId: result?.roundId || rounds[index]?.id || `round-${index + 1}`,
    score: Math.max(0, Math.floor(Number(result?.score) || 0)),
    failedChecks: Math.max(0, Math.floor(Number(result?.failedChecks) || 0)),
    difficultyKey: result?.difficultyKey || rounds[index]?.difficultyKey || '',
    interactionType: result?.interactionType || rounds[index]?.interactionType || '',
    response: Array.isArray(result?.response)
      ? result.response.map((value) => (Array.isArray(value) ? value.map(String) : String(value)))
      : [],
  }));

  return {
    completed: normalizedRoundResults.length === rounds.length && rounds.length > 0,
    archetype: archetype || rounds[0]?.archetype || '',
    roundsCompleted: normalizedRoundResults.length,
    totalRounds: rounds.length,
    perfectRounds: normalizedRoundResults.filter((result) => result.failedChecks === 0).length,
    failedChecks: normalizedRoundResults.reduce((sum, result) => sum + result.failedChecks, 0),
    score: normalizedRoundResults.reduce((sum, result) => sum + result.score, 0),
    sessionBadge: sessionBadge || '',
    roundResults: normalizedRoundResults,
  };
}
